import { useEffect, useState } from "react";
import { MapPin, Save, Globe, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, SectionCard } from "@/components/ui-helpers";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const toList = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);

export default function SellingAreas() {
  const [area, setArea] = useState(null);
  const [approved, setApproved] = useState(false);
  const [allIndia, setAllIndia] = useState(false);
  const [states, setStates] = useState("");
  const [districts, setDistricts] = useState("");
  const [pincodes, setPincodes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/marketplace/seller/selling-area").then((r) => {
      const a = r.data.selling_area; setArea(a); setApproved(r.data.approved);
      setAllIndia(!!a.all_india); setStates((a.states || []).join(", "));
      setDistricts((a.districts || []).join(", ")); setPincodes((a.pincodes || []).join(", "));
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = { all_india: allIndia, states: allIndia ? [] : toList(states), districts: allIndia ? [] : toList(districts), pincodes: allIndia ? [] : toList(pincodes) };
      const r = await api.put("/marketplace/seller/selling-area", body);
      setArea(r.data.selling_area);
      toast.success("Selling area saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const summary = allIndia ? "India → All India"
    : [toList(states).length && `${toList(states).length} state(s)`, toList(districts).length && `${toList(districts).length} district(s)`, toList(pincodes).length && `${toList(pincodes).length} pincode(s)`].filter(Boolean).join(" · ") || "No area set — your products won't be discoverable yet";

  return (
    <div data-testid="selling-areas-page">
      <PageHeader title="Selling Areas" icon={MapPin} subtitle="Decide where your products can be discovered and purchased. This is your default area; products can override it later." />

      {!approved && (
        <div className="mb-5 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700" data-testid="sa-not-approved">
          <AlertTriangle className="h-4 w-4" /> Your seller account isn't approved yet. You can configure your areas now — they'll take effect once you're verified.
        </div>
      )}

      <div className="mb-5 rounded-2xl p-5 text-white" style={{ backgroundColor: "#0b0a1f", backgroundImage: "radial-gradient(70% 60% at 15% 0%, rgba(37,99,235,.5), transparent 60%)" }}>
        <p className="text-xs uppercase tracking-wide text-white/60">Default selling area</p>
        <p className="mt-1 text-lg font-semibold" data-testid="sa-summary">{summary}</p>
      </div>

      <SectionCard title="Configure default area" icon={Globe} testid="sa-config">
        <label className="mb-4 flex cursor-pointer items-center gap-2">
          <input type="checkbox" checked={allIndia} onChange={(e) => setAllIndia(e.target.checked)} data-testid="sa-all-india" className="h-4 w-4 rounded" />
          <span className="text-sm font-medium text-slate-700">Sell across All India</span>
        </label>
        {!allIndia && (
          <div className="grid gap-3">
            <div><label className="text-xs font-medium text-slate-500">States (comma separated)</label>
              <Input value={states} onChange={(e) => setStates(e.target.value)} placeholder="e.g. Assam, Delhi" data-testid="sa-states" /></div>
            <div><label className="text-xs font-medium text-slate-500">Districts (comma separated)</label>
              <Input value={districts} onChange={(e) => setDistricts(e.target.value)} placeholder="e.g. Nalbari, Kamrup" data-testid="sa-districts" /></div>
            <div><label className="text-xs font-medium text-slate-500">Specific pincodes (comma separated)</label>
              <Input value={pincodes} onChange={(e) => setPincodes(e.target.value)} placeholder="e.g. 781335, 781001" data-testid="sa-pincodes" /></div>
            <p className="text-xs text-slate-400">A customer is served if their state, district or pincode matches any of the above. Broader areas (a state) automatically cover their districts.</p>
          </div>
        )}
        <button onClick={save} disabled={saving} data-testid="sa-save" className="mt-4 inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"><Save className="h-4 w-4" /> Save selling area</button>
        <p className="mt-3 text-xs text-slate-400">Product-specific areas can be set later from Product Management — products will inherit this default unless overridden.</p>
      </SectionCard>
    </div>
  );
}
